import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

// Type definitions
export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  avatar_url: string | null
}

export interface MemberUsage {
  request_count: number
  storage_used_mb: number
  last_active: string | null
}

export interface TeamMember {
  id: number
  user: User
  role: 'owner' | 'admin' | 'member'
  joined_at: string
  invited_at: string
  invited_by_user: User | null
  is_active: boolean
  permissions: Record<string, boolean>
  usage: MemberUsage
  is_current_user: boolean
}

export interface Invitation {
  id: number
  email: string
  role: 'admin' | 'member'
  token: string
  invited_by_user: User
  created_at: string
  expires_at: string
  accepted: boolean
  invitation_url: string
  expires_in_days: number
  is_expired: boolean
}

export interface TeamStats {
  total_members: number
  active_members: number
  pending_invitations: number
  total_requests_this_month: number
  total_storage_used_mb: number
}

export interface TeamDashboardData {
  members: TeamMember[]
  invitations: Invitation[]
  stats: TeamStats
  current_membership: TeamMember
  can_invite_members: boolean
  can_remove_members: boolean
  can_manage_team: boolean
  is_owner: boolean
}

/**
 * Hook to fetch complete team dashboard data
 */
export function useTeam() {
  return useQuery<TeamDashboardData>({
    queryKey: ['team'],
    queryFn: async () => {
      const response = await api.get('/team/dashboard/')
      return response.data
    },
  })
}

/**
 * Hook to invite a new member
 */
export function useInviteMember() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: { 
      email: string
      role: 'admin' | 'member'
      personal_message?: string 
    }) => {
      const response = await api.post('/team/invite/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

/**
 * Hook to cancel a pending invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (invitationId: number) => {
      const response = await api.post(`/team/invitations/${invitationId}/cancel/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

/**
 * Hook to remove a team member
 */
export function useRemoveMember() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (memberId: number) => {
      const response = await api.post(`/team/members/${memberId}/remove/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

/**
 * Hook to change a member's role
 */
export function useChangeRole() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ memberId, role }: { 
      memberId: number
      role: 'admin' | 'member' 
    }) => {
      const response = await api.post(`/team/members/${memberId}/change-role/`, { role })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
    },
  })
}

/**
 * Hook to transfer ownership to another member
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (memberId: number) => {
      const response = await api.post(`/team/members/${memberId}/transfer-ownership/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      // Also invalidate auth session since user's role changed
      queryClient.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

/**
 * Hook to accept an invitation (public endpoint)
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post(`/invitations/${token}/accept/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] })
      queryClient.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

/**
 * Hook to get invitation details (for unauthenticated users)
 */
export function useInvitationDetails(token: string) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const response = await api.get(`/invitations/${token}/details/`)
      return response.data
    },
    enabled: !!token,
  })
}
